"use client";

import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { CommentsKit } from "@tiptap-pro/extension-comments";
import { ExportMarkdown } from "@tiptap-pro/extension-export-markdown";
import { Snapshot } from "@tiptap-pro/extension-snapshot";
import { TrackedChanges } from "@tiptap-pro/extension-tracked-changes";
import { TiptapCollabProvider } from "@tiptap-pro/provider";
import type { Extensions, JSONContent } from "@tiptap/core";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { Selection } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import type { DecorationAttrs } from "@tiptap/pm/view";
import {
  EditorContent,
  EditorContext,
  useEditor,
  useEditorState,
  type Editor,
} from "@tiptap/react";
import { ServerAiToolkit } from "@tiptap/server-ai-toolkit";
import { StarterKit } from "@tiptap/starter-kit";
import { FilePenLineIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";

// --- UI Primitives ---
import { Button } from "src/tiptap/components/tiptap-ui-primitive/button";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "src/tiptap/components/tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import "src/tiptap/components/tiptap-node/blockquote-node/blockquote-node.css";
import "src/tiptap/components/tiptap-node/code-block-node/code-block-node.css";
import { createContentSchemaExtensions } from "src/tiptap/components/tiptap-node/content-schema-extensions";
import {
  ConditionalNode,
  ConditionalTableCell,
  ConditionalTableRow,
  ContextNode,
  DataKeyNode,
  DidNode,
  HandlebarsNode,
  SlashCommand,
} from "src/tiptap/components/tiptap-node/dynamic-nodes";
import { CONDITIONAL_NODE_NAME } from "src/tiptap/components/tiptap-node/dynamic-nodes/constants";
import { useDynamicNodeContext } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-context";
import {
  handleDynamicNodeMouseDown,
  hasNativeDynamicNodeTextSelection,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-selection";
import "src/tiptap/components/tiptap-node/heading-node/heading-node.css";
import "src/tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.css";
import "src/tiptap/components/tiptap-node/image-node/image-node.css";
import "src/tiptap/components/tiptap-node/list-node/list-node.css";
import "src/tiptap/components/tiptap-node/paragraph-node/paragraph-node.css";
import {
  TableCellHandleMenu,
  TableExtendRowColumnButtons,
  TableHandle,
  TableSelectionOverlay,
  TableTriggerButton,
} from "src/tiptap/components/tiptap-node/table-node";
import "src/tiptap/components/tiptap-node/table-node/styles/prosemirror-table.css";
import "src/tiptap/components/tiptap-node/table-node/styles/table-node.css";

// --- Tiptap UI ---
import { BlockquoteButton } from "src/tiptap/components/tiptap-ui/blockquote-button";
import { CodeBlockButton } from "src/tiptap/components/tiptap-ui/code-block-button";
import {
  ColorHighlightPopover,
  ColorHighlightPopoverButton,
  ColorHighlightPopoverContent,
} from "src/tiptap/components/tiptap-ui/color-highlight-popover";
import { HeadingDropdownMenu } from "src/tiptap/components/tiptap-ui/heading-dropdown-menu";
import { ImageUploadButton } from "src/tiptap/components/tiptap-ui/image-upload-button";
import {
  LinkButton,
  LinkContent,
  LinkPopover,
} from "src/tiptap/components/tiptap-ui/link-popover";
import { ListDropdownMenu } from "src/tiptap/components/tiptap-ui/list-dropdown-menu";
import { MarkButton } from "src/tiptap/components/tiptap-ui/mark-button";
import { TextAlignButton } from "src/tiptap/components/tiptap-ui/text-align-button";
import { UndoRedoButton } from "src/tiptap/components/tiptap-ui/undo-redo-button";

// --- Icons ---
import { ArrowLeftIcon } from "src/tiptap/components/tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "src/tiptap/components/tiptap-icons/highlighter-icon";
import { LinkIcon } from "src/tiptap/components/tiptap-icons/link-icon";

// --- Hooks ---
import { MarkdownPaste } from "src/tiptap/components/tiptap-extension/markdown-paste-extension";
import { NodeBackground } from "src/tiptap/components/tiptap-extension/node-background-extension";
import { useCursorVisibility } from "src/tiptap/hooks/use-cursor-visibility";
import { useIsBreakpoint } from "src/tiptap/hooks/use-is-breakpoint";
import { useWindowSize } from "src/tiptap/hooks/use-window-size";

// --- Lib ---
import { useUploadFileMutation } from "@hooks";
import { useSnackbar } from "src/components/Snackbar";
import {
  createTiptapThreadClickEvent,
  isTiptapSuggestionCommentThread,
  isTiptapUserCommentThread,
  TrackedChangesInputCompatibility,
  type TiptapCommentThread,
  type TiptapThreadClickEvent,
} from "src/components/TiptapEditor/collaboration";
import { useGetUser } from "src/hooks/queries";
import { createImageUploadKit } from "src/tiptap/components/tiptap-node/image-upload-node";
import { getReadyEditor } from "./editor-readiness";
import { getShareUrlFromUploadedFile } from "./image-upload";
import {
  SimpleEditorCollaborationSelectionToolbar,
  useSimpleEditorCollaboration,
  type SimpleEditorCollaborationExternalProps,
} from "./simple-editor-collaboration";
import { SimpleEditorLoadingSkeleton } from "./simple-editor-loading-skeleton";
import { type SimpleEditorVersionsController } from "./simple-editor-versions";
import { getVersionComparisonColorForUserId } from "./version-comparison/version-comparison-presentation";
import { ensureImportedDynamicNodeUserMapping } from "./yjs-attribution";

export { shouldDisableTiptapComments } from "./simple-editor-collaboration";
export type { SimpleEditorPreviewAction } from "./simple-editor-versions";
export type {
  SimpleEditorVersionComparisonAuthor,
  SimpleEditorVersionComparisonState,
} from "./version-comparison/version-comparison-presentation";

// --- Styles ---
import "src/tiptap/components/tiptap-templates/simple/simple-editor.css";

import content from "src/tiptap/components/tiptap-templates/simple/data/content.json";

export type SimpleEditorCollaborationConfig = {
  appId: string;
  documentName: string;
  token: string;
  userId: string;
};

type SimpleEditorProps = SimpleEditorCollaborationExternalProps & {
  agentThreadId?: string;
  collaborationConfig?: SimpleEditorCollaborationConfig;
  /**
   * Badge conditional table rows/cells (resolution-coloured stripe/ring, dim
   * when inactive) even though the editor is read-only — the authoring-style
   * treatment, used by the frames template preview. Off by default: a plain
   * read-only document view silently hides false conditional rows instead.
   */
  conditionalDecorationsInReadOnly?: boolean;
  deviceId?: string;
  documentContent?: JSONContent | null;
  documentId?: string;
  importedDynamicNodeAttributionUserId?: string | null;
  isLoading?: boolean;
  isReadOnly?: boolean;
  loadingLabel?: string;
  onSuggestionClick?: (suggestionId: string) => void;
  onVersionsContextChange?: (context: SimpleEditorVersionsContext) => void;
  orgId?: string;
  trackedChangesInitiallyEnabled?: boolean;
  versionController?: SimpleEditorVersionsController;
};

export type SimpleEditorVersionsContext = {
  collaborationProvider: TiptapCollabProvider | null;
  isEditable: boolean;
  readyEditor: Editor | null;
  trackedChangesUserId: string;
  trackedChangesUserName: string;
};

const EMPTY_DOCUMENT: JSONContent = { type: "doc", content: [] };
const EMPTY_ORG_USERS: NonNullable<
  SimpleEditorCollaborationExternalProps["orgUsers"]
> = [];
const EMPTY_SIMPLE_EDITOR_VERSIONS_CONTEXT: SimpleEditorVersionsContext = {
  collaborationProvider: null,
  isEditable: false,
  readyEditor: null,
  trackedChangesUserId: "current-user",
  trackedChangesUserName: "Current user",
};
const EMPTY_SIMPLE_EDITOR_VERSION_CONTROLLER: SimpleEditorVersionsController = {
  clearSelectedVersion: () => undefined,
  compareVersion: () => undefined,
  comparison: null,
  currentVersion: null,
  handleSnapshotUpdate: () => undefined,
  isVersionHistorySynced: true,
  isLoading: false,
  isVersionPreviewActive: false,
  previewAction: null,
  previewEditor: null,
  selectVersion: () => undefined,
  selectedVersion: null,
  versions: [],
  versionCards: null,
  versionComparison: null,
};

type TiptapThreadLookup = {
  getThread: (threadId: string) => TiptapCommentThread | null;
};

const isOpenableTiptapCollaborationThread = (thread: TiptapCommentThread) =>
  isTiptapUserCommentThread(thread) || isTiptapSuggestionCommentThread(thread);

export const getOpenableTiptapCommentThreadId = (
  threadId: string | null,
  provider: TiptapThreadLookup | null | undefined
) => {
  if (!threadId || !provider) {
    return null;
  }

  const thread = provider.getThread(threadId);

  return thread && isOpenableTiptapCollaborationThread(thread)
    ? thread.id
    : null;
};

type CollaborationStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "synced"
  | "error";

type CollaborationSession = {
  document: Y.Doc;
  provider: TiptapCollabProvider;
};

type SimpleEditorContentProps = SimpleEditorProps & {
  collaborationSession: CollaborationSession | null;
  collaborationStatus: CollaborationStatus;
};

const TRACKED_CHANGES_MODE_EVENT_NAMES = [
  "trackedChanges:enabled",
  "trackedChanges:disabled",
] as const;
const SUGGESTION_GROUP_SELECTOR = "[data-suggestion-id]";
const SUGGESTION_GROUP_HOVER_STYLE_ATTRIBUTE =
  "data-simple-editor-suggestion-hover-style";

const getSuggestionGroupElement = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLElement>(SUGGESTION_GROUP_SELECTOR)
    : null;

export const setSuggestionGroupHoverState = (
  root: HTMLElement,
  suggestionId: string,
  isHovered: boolean
) => {
  const existingStyle = root.querySelector<HTMLStyleElement>(
    `style[${SUGGESTION_GROUP_HOVER_STYLE_ATTRIBUTE}]`
  );

  if (!isHovered) {
    existingStyle?.remove();
    return 0;
  }

  let updatedCount = 0;

  for (const element of Array.from(
    root.querySelectorAll<HTMLElement>(SUGGESTION_GROUP_SELECTOR)
  )) {
    if (element.getAttribute("data-suggestion-id") !== suggestionId) {
      continue;
    }

    updatedCount += 1;
  }

  if (updatedCount === 0) {
    existingStyle?.remove();
    return 0;
  }

  const styleElement =
    existingStyle ?? root.ownerDocument.createElement("style");

  if (!existingStyle) {
    styleElement.setAttribute(SUGGESTION_GROUP_HOVER_STYLE_ATTRIBUTE, "true");
    root.appendChild(styleElement);
  }

  const escapedSuggestionId = suggestionId
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
  const suggestionSelector = `[data-suggestion-id="${escapedSuggestionId}"]`;

  styleElement.textContent = `
    .simple-editor-wrapper .tiptap ${suggestionSelector}[data-suggestion-type="add"]:not(img):not(hr),
    .simple-editor-wrapper .tiptap ${suggestionSelector}[data-suggestion-type="replaceInsertion"]:not(img):not(hr) {
      background-color: rgba(34, 197, 94, 0.2) !important;
    }

    .simple-editor-wrapper .tiptap ${suggestionSelector}[data-suggestion-type="delete"]:not(img):not(hr),
    .simple-editor-wrapper .tiptap ${suggestionSelector}[data-suggestion-type="replaceDeletion"]:not(img):not(hr) {
      background-color: rgba(239, 68, 68, 0.14) !important;
    }
  `;

  return updatedCount;
};

type CreateEditorExtensionsOptions = {
  additionalExtensions?: Extensions;
  commentsExtension: Extensions[number];
  disableUndoRedo?: boolean;
  imageExtensions: Extensions;
  trackedChangesEnabled: boolean;
  trackedChangesUserId: string;
  trackedChangesUserName: string;
};

type EditorWithTrackedChangesStorage = Editor & {
  storage: Editor["storage"] & {
    trackedChanges?: {
      enabled?: boolean;
    };
  };
};

const createStarterKitExtension = (disableUndoRedo = false) =>
  StarterKit.configure({
    horizontalRule: false,
    ...(disableUndoRedo ? { undoRedo: false } : {}),
    link: {
      openOnClick: false,
      enableClickSelection: true,
    },
  });

const createEditorContentExtensions = (
  imageExtensions: Extensions
): Extensions => [
  ServerAiToolkit,
  NodeBackground,
  Selection,
  ...createContentSchemaExtensions({ image: imageExtensions }),
];

const createEditorExtensions = ({
  additionalExtensions = [],
  commentsExtension,
  disableUndoRedo = false,
  imageExtensions,
  trackedChangesEnabled,
  trackedChangesUserId,
  trackedChangesUserName,
}: CreateEditorExtensionsOptions): Extensions => [
  createStarterKitExtension(disableUndoRedo),
  ...additionalExtensions,
  TrackedChangesInputCompatibility,
  TrackedChanges.configure({
    enabled: trackedChangesEnabled,
    userId: trackedChangesUserId,
    userMetadata: {
      name: trackedChangesUserName,
    },
  }),
  commentsExtension,
  ...createEditorContentExtensions(imageExtensions),
  Markdown,
  MarkdownPaste,
  ExportMarkdown.configure({
    onCompleteExport: () => undefined,
  }),
];

const isTrackedChangesEnabledForEditor = (editor: Editor | null) =>
  Boolean(
    (editor as EditorWithTrackedChangesStorage | null)?.storage.trackedChanges
      ?.enabled
  );

export const getTiptapCollaborationColorForUserId = (userId: string) =>
  getVersionComparisonColorForUserId(userId);

type TiptapCollaborationUserColorInput = {
  id?: string | null;
  name?: string | null;
  userId?: string | null;
};

const getTiptapCollaborationString = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export const getTiptapCollaborationColorForUser = ({
  id,
  name,
  userId,
}: TiptapCollaborationUserColorInput) => {
  const identityKey = [id ?? userId, name]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(":");

  return getVersionComparisonColorForUserId(identityKey || "current-user");
};

const getTiptapCollaborationColorFromAwarenessUser = (
  user: Record<string, unknown>
) =>
  getTiptapCollaborationColorForUser({
    id: getTiptapCollaborationString(user.id),
    name: getTiptapCollaborationString(user.name),
    userId: getTiptapCollaborationString(user.userId),
  });

export const renderTiptapCollaborationCaret = (
  user: Record<string, unknown>
) => {
  const color = getTiptapCollaborationColorFromAwarenessUser(user);
  const name = getTiptapCollaborationString(user.name) ?? "Collaborator";
  const cursor = document.createElement("span");

  cursor.classList.add("collaboration-carets__caret");
  cursor.style.borderColor = color;

  const label = document.createElement("div");

  label.classList.add("collaboration-carets__label");
  label.style.backgroundColor = color;
  label.textContent = name;
  cursor.insertBefore(label, null);

  return cursor;
};

export const renderTiptapCollaborationSelection = (
  user: Record<string, unknown>
): DecorationAttrs => {
  const color = getTiptapCollaborationColorFromAwarenessUser(user);

  return {
    class: "ProseMirror-yjs-selection",
    style: `background-color: ${color}70`,
  };
};

const MainToolbarContent = ({
  hasConditionals,
  isTrackedChangesEnabled,
  isTrackedChangesToggleDisabled,
  onHighlighterClick,
  onLinkClick,
  onTrackedChangesToggle,
  isImageUploading,
  isMobile,
}: {
  hasConditionals: boolean;
  isTrackedChangesEnabled: boolean;
  isTrackedChangesToggleDisabled: boolean;
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  onTrackedChangesToggle: () => void;
  isImageUploading: boolean;
  isMobile: boolean;
}) => {
  const { showAllConditionals, onShowAllConditionalsChange } =
    useDynamicNodeContext();
  const conditionalVisibilityLabel = `${
    showAllConditionals ? "Hide" : "Show"
  } false conditional values`;
  const trackedChangesLabel = isTrackedChangesEnabled
    ? "Stop suggesting"
    : "Suggest changes";

  return (
    <>
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        {hasConditionals ? (
          <Button
            type="button"
            variant="ghost"
            className="simple-editor-conditional-visibility-button"
            data-active-state={showAllConditionals ? "on" : "off"}
            role="button"
            tabIndex={-1}
            aria-label={conditionalVisibilityLabel}
            aria-pressed={showAllConditionals}
            tooltip={conditionalVisibilityLabel}
            onClick={() => onShowAllConditionalsChange(!showAllConditionals)}
          >
            {showAllConditionals ? (
              <VisibilityOutlined className="tiptap-button-icon" />
            ) : (
              <VisibilityOffOutlined className="tiptap-button-icon" />
            )}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          className="simple-editor-tracked-changes-button"
          disabled={isTrackedChangesToggleDisabled}
          data-active-state={isTrackedChangesEnabled ? "on" : "off"}
          data-disabled={isTrackedChangesToggleDisabled}
          role="button"
          tabIndex={-1}
          aria-label={trackedChangesLabel}
          aria-pressed={isTrackedChangesEnabled}
          tooltip={trackedChangesLabel}
          onClick={onTrackedChangesToggle}
        >
          <FilePenLineIcon className="tiptap-button-icon" />
        </Button>
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu modal={false} levels={[1, 2, 3, 4]} />
        <ListDropdownMenu
          modal={false}
          types={["bulletList", "orderedList", "taskList"]}
        />
        <BlockquoteButton />
        <CodeBlockButton />
        <TableTriggerButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton text="Add" isUploading={isImageUploading} />
      </ToolbarGroup>
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: "highlighter" | "link";
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
);

const useCollaborationSession = (config?: SimpleEditorCollaborationConfig) => {
  const [session, setSession] = useState<CollaborationSession | null>(null);
  const [status, setStatus] = useState<CollaborationStatus>("idle");

  useEffect(() => {
    if (!config) {
      setSession(null);
      setStatus("idle");
      return;
    }

    let isActive = true;
    let provider: TiptapCollabProvider | null = null;
    const collaborationDocument = new Y.Doc();
    const collaborationUsers = collaborationDocument.getMap<Y.Map<unknown>>(
      "__tiptapcollab__users"
    );
    // Transport sync only means the remote Yjs state has been merged. Tiptap's
    // PermanentUserData still needs to persist this client's Formly user mapping,
    // and a reconnect can temporarily overwrite that Y.Map while the provider's
    // in-memory cache remains stale. Editing during that window produces versions
    // with no `changesBy`, so keep the editor locked until the persisted map proves
    // that the current client ID belongs to the authenticated user.
    const verifyAuthorship = () => {
      const currentProvider = provider;

      if (!isActive || !currentProvider?.synced) {
        return;
      }

      if (!currentProvider.userData) {
        console.error("Unable to establish Tiptap collaboration authorship");
        setStatus("error");
        return;
      }

      const persistedClientIds = collaborationUsers
        .get(config.userId)
        ?.get("ids");

      if (
        persistedClientIds instanceof Y.Array &&
        persistedClientIds.toArray().includes(collaborationDocument.clientID)
      ) {
        setStatus("synced");
      }
    };

    setSession(null);
    setStatus("connecting");
    collaborationUsers.observeDeep(verifyAuthorship);

    try {
      provider = new TiptapCollabProvider({
        name: config.documentName,
        appId: config.appId,
        token: config.token,
        document: collaborationDocument,
        user: config.userId,
        onConnect: () => {
          if (isActive) {
            setStatus("connected");
          }
        },
        onSynced: ({ state }) => {
          if (isActive) {
            // Verify immediately when already reconciled; otherwise the map
            // observer reruns verification when PermanentUserData restores it.
            setStatus("connected");
            if (state) {
              verifyAuthorship();
            }
          }
        },
        onAuthenticationFailed: () => {
          if (isActive) {
            setStatus("error");
          }
        },
        onClose: () => {
          if (isActive) {
            setStatus((currentStatus) =>
              currentStatus === "error" ? currentStatus : "idle"
            );
          }
        },
      });
    } catch (error) {
      console.error("Unable to start Tiptap collaboration provider", error);
      collaborationDocument.destroy();
      setStatus("error");
      return;
    }

    setSession({ document: collaborationDocument, provider });

    return () => {
      isActive = false;
      collaborationUsers.unobserveDeep(verifyAuthorship);
      provider?.destroy();
      collaborationDocument.destroy();
    };
  }, [config?.appId, config?.documentName, config?.token, config?.userId]);

  return { session, status };
};

export function SimpleEditor(props: SimpleEditorProps) {
  const { session, status } = useCollaborationSession(
    props.collaborationConfig
  );

  if (props.collaborationConfig && !session) {
    return (
      <div className="simple-editor-wrapper">
        <div className="simple-editor-workspace" aria-busy={status !== "error"}>
          {status === "error" ? (
            <div className="simple-editor-connection-error" role="alert">
              {`Unable to connect ${props.documentId ?? "document"}`}
            </div>
          ) : (
            <SimpleEditorLoadingSkeleton
              label={`Connecting ${props.documentId ?? "document"}`}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <SimpleEditorContent
      {...props}
      collaborationSession={session}
      collaborationStatus={status}
    />
  );
}

function SimpleEditorContent({
  areCommentMutationsDisabled = false,
  collaborationConfig,
  collaborationSession,
  collaborationStatus,
  conditionalDecorationsInReadOnly = false,
  deviceId,
  documentContent,
  documentId,
  importedDynamicNodeAttributionUserId,
  isLoading = false,
  isReadOnly = false,
  loadingLabel,
  onCommentCreated,
  onCreateVersionHandlerChange,
  onCommentsControllerChange,
  onOpenCommentsPanel,
  onSuggestionClick,
  onSuggestionsControllerChange,
  orgUsers = EMPTY_ORG_USERS,
  onVersionsContextChange,
  orgId = "",
  trackedChangesInitiallyEnabled = false,
  versionController = EMPTY_SIMPLE_EDITOR_VERSION_CONTROLLER,
}: SimpleEditorContentProps) {
  const isMobile = useIsBreakpoint();
  const { height } = useWindowSize();
  const { data: user } = useGetUser();
  const [mobileView, setMobileView] = useState<"main" | "highlighter" | "link">(
    "main"
  );
  const [isTrackedChangesEnabled, setIsTrackedChangesEnabled] = useState(
    trackedChangesInitiallyEnabled
  );
  const [clickedThread, setClickedThread] =
    useState<TiptapThreadClickEvent | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const loadedDocumentKeyRef = useRef<string | null>(null);
  const trackedChangesUserId =
    user?.id ?? collaborationConfig?.userId ?? "current-user";
  const trackedChangesUserName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Current user";
  const collaborationCaretUser = useMemo(
    () => ({
      id: trackedChangesUserId,
      name: trackedChangesUserName,
      color: getTiptapCollaborationColorForUser({
        id: trackedChangesUserId,
        name: trackedChangesUserName,
      }),
    }),
    [trackedChangesUserId, trackedChangesUserName]
  );
  const documentContentKey = useMemo(
    () => (documentContent ? JSON.stringify(documentContent) : ""),
    [documentContent]
  );
  const isCollaborative = !!collaborationSession;
  const { showSnackbar } = useSnackbar();
  const { mutateAsync: uploadFile, isPending: isImageUploading } =
    useUploadFileMutation();
  const onImageUploadError = useCallback(
    (error: Error) =>
      showSnackbar(error.message || "Image upload failed. Please try again."),
    [showSnackbar]
  );
  const uploadImage = useCallback(
    async (file: File, uploadOrgId: string) => {
      const uploadResult = await uploadFile({
        orgId: uploadOrgId,
        file,
        deviceId: deviceId || undefined,
      });

      return {
        url: getShareUrlFromUploadedFile(uploadResult),
      };
    },
    [deviceId, uploadFile]
  );
  const imageExtensions = useMemo(
    () =>
      createImageUploadKit({
        uploadImage: (file) => uploadImage(file, orgId),
        onError: onImageUploadError,
      }).extensions,
    [onImageUploadError, orgId, uploadImage]
  );

  // With `immediatelyRender: false` (and whenever the editor is recreated as the
  // collaboration session/options settle) `useEditor` briefly returns an editor
  // instance whose view/commandManager/extensionManager are not mounted yet.
  // Toolbar buttons call `editor.can()` / `editor.extensionManager` straight
  // away, so we only render them once the editor reports it is created.
  const [readyEditor, setReadyEditor] = useState<Editor | null>(null);
  const editorVersions = versionController;
  const { isVersionPreviewActive, previewEditor, versionComparison } =
    editorVersions;
  const isEditorPreviewModeActive = isVersionPreviewActive;
  const isEditorEditable =
    Boolean(readyEditor) &&
    (!isCollaborative || collaborationStatus === "synced") &&
    !isReadOnly &&
    !isEditorPreviewModeActive;
  const versionsContext = useMemo<SimpleEditorVersionsContext>(
    () => ({
      collaborationProvider: collaborationSession?.provider ?? null,
      isEditable: isEditorEditable,
      readyEditor,
      trackedChangesUserId,
      trackedChangesUserName,
    }),
    [
      collaborationSession?.provider,
      isEditorEditable,
      readyEditor,
      trackedChangesUserId,
      trackedChangesUserName,
    ]
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable: !isCollaborative && !isReadOnly && !isEditorPreviewModeActive,
      onCreate: ({ editor: currentEditor }) => {
        setReadyEditor(null);
        window.setTimeout(() => {
          const usableEditor = getReadyEditor(currentEditor);

          if (!usableEditor) {
            return;
          }

          setReadyEditor(usableEditor);
        }, 0);
      },
      onDestroy: () => {
        setReadyEditor(null);
      },
      editorProps: {
        handleDOMEvents: {
          mousedown: handleDynamicNodeMouseDown,
          // Dynamic nodes store a key or template, so copy their visible DOM text.
          copy: (view) =>
            hasNativeDynamicNodeTextSelection(
              view.dom,
              view.dom.ownerDocument.getSelection()
            ),
        },
        attributes: {
          autocomplete: "off",
          autocorrect: "off",
          autocapitalize: "off",
          "aria-label": "Main content area, start typing to enter text.",
          class: "simple-editor",
        },
      },
      extensions: createEditorExtensions({
        additionalExtensions: [
          ...(collaborationSession
            ? [
                Collaboration.configure({
                  document: collaborationSession.document,
                  provider: collaborationSession.provider,
                }),
                CollaborationCaret.configure({
                  provider: collaborationSession.provider,
                  user: collaborationCaretUser,
                  render: renderTiptapCollaborationCaret,
                  selectionRender: renderTiptapCollaborationSelection,
                }),
                Snapshot.configure({
                  provider: collaborationSession.provider,
                  onUpdate: (payload) => {
                    editorVersions.handleSnapshotUpdate(payload.versions);
                  },
                }),
              ]
            : []),
          DataKeyNode,
          ContextNode,
          DidNode,
          ConditionalNode,
          ConditionalTableRow.configure({
            decorateReadOnly: conditionalDecorationsInReadOnly,
          }),
          ConditionalTableCell.configure({
            decorateReadOnly: conditionalDecorationsInReadOnly,
          }),
          HandlebarsNode,
          SlashCommand,
        ],
        commentsExtension: CommentsKit.configure({
          provider: collaborationSession?.provider ?? null,
          deleteUnreferencedThreads: false,
          trackedChangesIntegration: isCollaborative,
          onClickThread: (threadId) => {
            const openableThreadId = getOpenableTiptapCommentThreadId(
              threadId,
              collaborationSession?.provider as TiptapThreadLookup | null
            );

            setClickedThread((previousEvent) =>
              createTiptapThreadClickEvent(openableThreadId, previousEvent)
            );

            if (openableThreadId) {
              onOpenCommentsPanel?.();
            }
          },
        }),
        disableUndoRedo: isCollaborative,
        imageExtensions,
        trackedChangesEnabled: trackedChangesInitiallyEnabled,
        trackedChangesUserId,
        trackedChangesUserName,
      }),
      content: isCollaborative
        ? undefined
        : documentId && !documentContent
          ? EMPTY_DOCUMENT
          : (documentContent ?? content),
    },
    [
      collaborationSession,
      collaborationCaretUser,
      conditionalDecorationsInReadOnly,
      editorVersions.handleSnapshotUpdate,
      isCollaborative,
      onOpenCommentsPanel,
      trackedChangesInitiallyEnabled,
      trackedChangesUserId,
      trackedChangesUserName,
      imageExtensions,
    ]
  );

  useEffect(() => {
    getReadyEditor(readyEditor)?.setEditable(isEditorEditable);
  }, [isEditorEditable, readyEditor]);

  useEffect(() => {
    onVersionsContextChange?.(versionsContext);
  }, [onVersionsContextChange, versionsContext]);

  useEffect(
    () => () => {
      onVersionsContextChange?.(EMPTY_SIMPLE_EDITOR_VERSIONS_CONTEXT);
    },
    [onVersionsContextChange]
  );

  const collaborationEditor = getReadyEditor(readyEditor);
  const hasConditionals =
    useEditorState({
      editor: collaborationEditor,
      selector: ({ editor: currentEditor }) => {
        let found = false;

        currentEditor?.state.doc.descendants((node) => {
          found ||=
            node.type.name === CONDITIONAL_NODE_NAME ||
            (node.type.name === "tableRow" && Boolean(node.attrs.condition));

          return !found;
        });

        return found;
      },
    }) ?? false;

  const editorCollaboration = useSimpleEditorCollaboration({
    areCommentMutationsDisabled,
    clickedThread,
    currentUserEmail: user?.email ?? null,
    currentUserId: trackedChangesUserId,
    currentUserName: trackedChangesUserName,
    editor: collaborationEditor,
    isCollaborative,
    isReadOnly,
    isSavingOfficialVersion: false,
    onCommentCreated,
    onCommentsControllerChange,
    onCreateVersionHandlerChange,
    onOpenCommentsPanel,
    onSuggestionsControllerChange,
    orgUsers,
    provider: collaborationSession?.provider ?? null,
  });
  const { commentsController } = editorCollaboration;

  useEffect(() => {
    if (
      collaborationStatus !== "synced" ||
      !collaborationSession ||
      !importedDynamicNodeAttributionUserId
    ) {
      return;
    }

    ensureImportedDynamicNodeUserMapping({
      document: collaborationSession.document,
      permanentUserData: collaborationSession.provider.userData,
      userId: importedDynamicNodeAttributionUserId,
    });
  }, [
    collaborationSession,
    collaborationStatus,
    importedDynamicNodeAttributionUserId,
  ]);

  const refreshTrackedChangesMode = useCallback(() => {
    setIsTrackedChangesEnabled(
      isTrackedChangesEnabledForEditor(getReadyEditor(readyEditor))
    );
  }, [readyEditor]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor) {
      setIsTrackedChangesEnabled(false);
      return;
    }

    refreshTrackedChangesMode();

    TRACKED_CHANGES_MODE_EVENT_NAMES.forEach((eventName) => {
      currentEditor.on(eventName, refreshTrackedChangesMode);
    });

    return () => {
      TRACKED_CHANGES_MODE_EVENT_NAMES.forEach((eventName) => {
        currentEditor.off(eventName, refreshTrackedChangesMode);
      });
    };
  }, [refreshTrackedChangesMode]);

  const toggleTrackedChanges = useCallback(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor) {
      return;
    }

    const nextEnabled = !isTrackedChangesEnabledForEditor(currentEditor);
    const success = nextEnabled
      ? currentEditor.commands.enableTrackedChanges()
      : currentEditor.commands.disableTrackedChanges();

    if (success) {
      setIsTrackedChangesEnabled(nextEnabled);
    }
  }, [readyEditor]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor) {
      return;
    }

    currentEditor.commands.setTrackedChangesUser({
      userId: trackedChangesUserId,
      userMetadata: {
        name: trackedChangesUserName,
      },
    });
  }, [readyEditor, trackedChangesUserId, trackedChangesUserName]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor || isLoading || isCollaborative) {
      return;
    }

    if (documentId && !documentContent) {
      return;
    }

    const documentKey = documentId
      ? `${documentId}:${documentContentKey}`
      : "__static_simple_editor_content__";
    if (loadedDocumentKeyRef.current === documentKey) {
      return;
    }

    const nextDocument = currentEditor.schema.nodeFromJSON(
      documentContent ?? content
    );
    const transaction = currentEditor.state.tr
      .replaceWith(
        0,
        currentEditor.state.doc.content.size,
        nextDocument.content
      )
      .setMeta("trackedChanges", { type: "skipTracking" })
      .setMeta("addToHistory", false);

    currentEditor.view.dispatch(transaction);
    loadedDocumentKeyRef.current = documentKey;
  }, [
    documentContent,
    documentContentKey,
    documentId,
    isCollaborative,
    isLoading,
    readyEditor,
  ]);

  const rect = useCursorVisibility({
    editor: collaborationEditor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main");
    }
  }, [isMobile, mobileView]);

  useEffect(() => {
    const root = wrapperRef.current;

    if (!root) {
      return;
    }

    const handleGroupEnter = (event: Event) => {
      const suggestionElement = getSuggestionGroupElement(event.target);
      const suggestionId =
        suggestionElement?.getAttribute("data-suggestion-id");

      if (!suggestionId) {
        setSuggestionGroupHoverState(root, "", false);
        return;
      }

      setSuggestionGroupHoverState(root, suggestionId, true);
    };

    const clearGroupHover = () => {
      setSuggestionGroupHoverState(root, "", false);
    };

    const handleGroupLeave = (event: Event) => {
      const suggestionElement = getSuggestionGroupElement(event.target);
      const suggestionId =
        suggestionElement?.getAttribute("data-suggestion-id");

      if (!suggestionId) {
        return;
      }

      const nextSuggestionElement = getSuggestionGroupElement(
        "relatedTarget" in event
          ? (event.relatedTarget as EventTarget | null)
          : null
      );

      if (
        nextSuggestionElement?.getAttribute("data-suggestion-id") ===
        suggestionId
      ) {
        return;
      }

      setSuggestionGroupHoverState(root, suggestionId, false);
    };

    const handleSuggestionClick = (event: MouseEvent) => {
      const suggestionElement = getSuggestionGroupElement(event.target);
      const suggestionId =
        suggestionElement?.getAttribute("data-suggestion-id");

      if (!suggestionId || !onSuggestionClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onSuggestionClick(suggestionId);
    };

    root.addEventListener("pointerover", handleGroupEnter);
    root.addEventListener("pointerout", handleGroupLeave);
    root.addEventListener("pointerleave", clearGroupHover);
    root.addEventListener("click", handleSuggestionClick, true);
    root.addEventListener("focusin", handleGroupEnter);
    root.addEventListener("focusout", handleGroupLeave);

    return () => {
      root.removeEventListener("pointerover", handleGroupEnter);
      root.removeEventListener("pointerout", handleGroupLeave);
      root.removeEventListener("pointerleave", clearGroupHover);
      root.removeEventListener("click", handleSuggestionClick, true);
      root.removeEventListener("focusin", handleGroupEnter);
      root.removeEventListener("focusout", handleGroupLeave);
      root
        .querySelector<HTMLStyleElement>(
          `style[${SUGGESTION_GROUP_HOVER_STYLE_ATTRIBUTE}]`
        )
        ?.remove();
    };
  }, [onSuggestionClick]);

  const editorContextValue = useMemo(
    () => ({ editor: isEditorPreviewModeActive ? null : collaborationEditor }),
    [collaborationEditor, isEditorPreviewModeActive]
  );

  return (
    <div className="simple-editor-wrapper" ref={wrapperRef}>
      <EditorContext.Provider value={editorContextValue}>
        {!isLoading && !isReadOnly && !isEditorPreviewModeActive ? (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${height - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {collaborationEditor ? (
              mobileView === "main" ? (
                <MainToolbarContent
                  hasConditionals={hasConditionals}
                  isTrackedChangesEnabled={isTrackedChangesEnabled}
                  isTrackedChangesToggleDisabled={
                    !collaborationEditor.isEditable
                  }
                  onHighlighterClick={() => setMobileView("highlighter")}
                  onLinkClick={() => setMobileView("link")}
                  onTrackedChangesToggle={toggleTrackedChanges}
                  isImageUploading={isImageUploading}
                  isMobile={isMobile}
                />
              ) : (
                <MobileToolbarContent
                  type={mobileView === "highlighter" ? "highlighter" : "link"}
                  onBack={() => setMobileView("main")}
                />
              )
            ) : null}
          </Toolbar>
        ) : null}

        <div className="simple-editor-workspace" aria-busy={isLoading}>
          <div
            role="presentation"
            aria-hidden={isEditorPreviewModeActive}
            data-version-comparison-active={Boolean(versionComparison)}
            className={`simple-editor-content${
              isEditorPreviewModeActive ? " simple-editor-content--hidden" : ""
            }`}
          >
            <EditorContent
              editor={editor}
              className="simple-editor-editor-content"
            />
            <TableHandle editor={editor} />
            <TableSelectionOverlay
              editor={editor}
              showResizeHandles
              cellMenu={({ onResizeStart, ...props }) => (
                <TableCellHandleMenu
                  {...props}
                  onMouseDown={(event) => onResizeStart?.("br")(event)}
                />
              )}
            />
            <TableExtendRowColumnButtons editor={editor} />
          </div>
          {!isLoading && !isEditorPreviewModeActive ? (
            <SimpleEditorCollaborationSelectionToolbar
              controller={commentsController}
              editor={editor}
            />
          ) : null}
          {isEditorPreviewModeActive ? (
            <section
              aria-label="Version preview"
              className="simple-editor-inline-version-preview"
            >
              {versionComparison && versionComparison.authors.length > 0 ? (
                <div
                  aria-label="Comparison authors"
                  className="simple-editor-diff-legend"
                >
                  {versionComparison.authors.map((author) => (
                    <span
                      className="simple-editor-diff-legend__item"
                      key={author.id}
                    >
                      <span
                        aria-hidden="true"
                        className="simple-editor-diff-legend__swatch"
                        style={{ backgroundColor: author.color }}
                      />
                      {author.name}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="simple-editor-inline-version-preview__content">
                <EditorContent
                  editor={previewEditor}
                  className="simple-editor-inline-version-preview__editor"
                />
              </div>
            </section>
          ) : null}
          {isLoading ? (
            <SimpleEditorLoadingSkeleton
              label={loadingLabel ?? `Loading ${documentId ?? "document"}`}
            />
          ) : null}
        </div>
      </EditorContext.Provider>
    </div>
  );
}
